import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Receipt, ArrowClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

export default function VendorTransactionsPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/vendors/${user.id}/entries`);
      setEntries(r.data || []);
    } catch (err) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = statusFilter === 'all' ? entries : entries.filter((e) => e.status === statusFilter);
  const byStatus = entries.reduce((a, e) => { a[e.status || 'pending'] = (a[e.status || 'pending'] || 0) + 1; return a; }, {});
  const totals = filtered.reduce((a, e) => ({
    face: a.face + (e.total_face_value || 0),
    payable: a.payable + (e.total_payable || 0),
  }), { face: 0, payable: 0 });

  return (
    <div className="space-y-5" data-testid="vendor-transactions-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Receipt size={26} weight="duotone" className="text-indigo-600" /> Transaction History
          <span className="text-sm font-normal text-gray-500 ml-1">({entries.length})</span>
        </h1>
        <Button size="sm" variant="outline" className="ml-auto" onClick={load}><ArrowClockwise size={14} /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['pending', 'Pending', 'amber'],
          ['approved', 'Approved', 'emerald'],
          ['rejected', 'Rejected', 'red'],
        ].map(([k, label, c]) => (
          <div key={k} className={`bg-${c}-50 border border-${c}-200 rounded-lg p-3`}>
            <div className={`text-[10px] uppercase text-${c}-700 font-semibold`}>{label}</div>
            <div className={`text-xl font-bold text-${c}-900`}>{byStatus[k] || 0}</div>
          </div>
        ))}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <div className="text-[10px] uppercase text-indigo-700 font-semibold">Total Submissions</div>
          <div className="text-xl font-bold text-indigo-900">{entries.length}</div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-3 flex gap-2 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs font-semibold rounded border ${statusFilter === s ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300'}`}
            data-testid={`trans-filter-${s}`}>
            {s.toUpperCase()} {s !== 'all' && byStatus[s] ? `(${byStatus[s]})` : ''}
          </button>
        ))}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-indigo-50 border-b text-xs flex justify-between flex-wrap gap-2">
          <span>Showing <b>{filtered.length}</b></span>
          <span>Total Face: <b>{fmt(totals.face)}</b> • Total Payable: <b className="text-emerald-700">{fmt(totals.payable)}</b></span>
        </div>
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : filtered.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">Koi submission abhi tak nahi</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Cards × ₹</th>
                  <th className="px-3 py-2 text-right">Face</th>
                  <th className="px-3 py-2 text-right">Discount %</th>
                  <th className="px-3 py-2 text-right">Payable</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Remarks</th>
                  <th className="px-3 py-2 text-left">Review Notes</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-t hover:bg-gray-50" data-testid={`trans-row-${e.id}`}>
                    <td className="px-3 py-1.5 text-xs">{fmtDate(e.created_at)}</td>
                    <td className="px-3 py-1.5 text-xs">{e.card_count} × {fmt(e.per_card_amount || 0)}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{fmt(e.total_face_value || 0)}</td>
                    <td className="px-3 py-1.5 text-right">{e.discount_percent || 0}%</td>
                    <td className="px-3 py-1.5 text-right text-emerald-700 font-semibold">{fmt(e.total_payable || 0)}</td>
                    <td className="px-3 py-1.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        e.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        e.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{e.status?.toUpperCase() || 'PENDING'}</span>
                    </td>
                    <td className="px-3 py-1.5 text-xs text-gray-600 max-w-[180px] truncate">{e.remarks || '—'}</td>
                    <td className="px-3 py-1.5 text-xs text-gray-600 max-w-[200px] truncate">{e.review_notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
